/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { TicketListRow } from '../../shared/ticket/TicketListRow'
import { ticketColors } from '../../shared/theme/ticketColors';
import { resolveInventoryStockStatusPresentation } from '../application/inventoryStockStatusPresentation';
import { computeStockRowValue, formatStockQuantity } from '../application/inventoryUtils';
import { getStockLotExpirySplit } from '../../inventory/stock/public-api';
import type { StockRow } from '../../inventory/stock/public-api';

type Props = {
  item: StockRow;
  onDetails: (row: StockRow) => void;
  /** Mostrar costo y valor en la meta (permiso canViewValuation). */
  showValuation?: boolean;
  /** Formateador de moneda para costo/valor. */
  formatCurrency?: (v: number) => string;
  /** #801: Es una fila de variante (indentada, muestra label de variante). */
  isVariant?: boolean;
  /** #801: Tiene variantes expandibles (muestra chevron de expandir). */
  hasVariants?: boolean;
  /** #801: Si la fila parent está expandida. */
  expanded?: boolean;
  /** #801: Callback para expandir/contraer. */
  onToggleExpand?: () => void;
};

function InventoryStockCardInner({
  item,
  onDetails,
  showValuation = false,
  formatCurrency,
  isVariant = false,
  hasVariants = false,
  expanded = false,
  onToggleExpand,
}: Props) {
  const { label, tone, meta } = resolveInventoryStockStatusPresentation(item);
  // Split FEFO por lotes: el agregado usa MIN(expires_at) → un solo lote vencido
  // pintaba TODA la cantidad como «Vencido». Con lotes, vendible ≠ total.
  // Las filas de variante no tienen lotes propios (FEFO a nivel producto).
  const lotSplit = isVariant
    ? null
    : getStockLotExpirySplit(item.productId, item.branchId);
  const metaParts: string[] = [];
  // #801: Para filas de variante, mostrar variantSku si difiere del productSku.
  if (isVariant && item.variantSku) {
    metaParts.push(item.variantSku);
  } else if (item.productSku) {
    metaParts.push(item.productSku);
  }
  if (item.productBrand) metaParts.push(item.productBrand);
  if (meta) metaParts.push(meta);
  // Split solo cuando es mixto: 100% vencido ya lo dice el badge + «venció DD/MM».
  if (lotSplit && lotSplit.expiredQuantity > 0 && lotSplit.sellableQuantity > 0) {
    metaParts.push(
      `${formatStockQuantity(lotSplit.sellableQuantity, item.unitOfMeasure)} vendibles · ` +
        `${formatStockQuantity(lotSplit.expiredQuantity, item.unitOfMeasure)} vencidas`
    );
  }
  if (showValuation && formatCurrency) {
    const costParts: string[] = [];
    if (item.avgCost > 0) costParts.push(`Costo ${formatCurrency(item.avgCost)}`);
    // Multiplicación exacta en centavos — el valor mostrado nunca diverge del
    // «cantidad × costo» por polvo de punto flotante.
    const stockValue = computeStockRowValue(item.quantity, item.avgCost);
    if (stockValue > 0) costParts.push(`valor ${formatCurrency(stockValue)}`);
    if (costParts.length > 0) metaParts.push(costParts.join(' · '));
  }
  const mixed = Boolean(
    lotSplit && lotSplit.expiredQuantity > 0 && lotSplit.sellableQuantity > 0
  );
  // Fila sana = sin pill: solo los estados problema llevan etiqueta visible.
  const statusLabel =
    item.quantity === 0 ? 'Sin stock' : mixed ? 'Vencido parcial' : label === 'OK' ? null : label;
  const statusTone =
    tone === 'critical' || item.quantity <= 0 ? 'bad' : tone === 'warning' ? 'warn' : 'ok';
  const qtyLabel = formatStockQuantity(item.quantity, item.unitOfMeasure);

  // #801: Título para variante = "Producto · Label"; subtítulo para parent con variantes.
  const title = isVariant
    ? `${item.productName} · ${item.variantLabel ?? 'Variante'}`
    : item.productName;
  const subtitle = isVariant ? 'Variante' : null;

  // #801: Expand/collapse chevron for parent rows with variants.
  const expandButton =
    hasVariants && onToggleExpand ? (
      <TouchableOpacity
        onPress={onToggleExpand}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel={expanded ? 'Contraer variantes' : 'Expandir variantes'}
        testID={`stock.expand.${item.productId}`}
      >
        <Ionicons
          name={expanded ? 'chevron-down' : 'chevron-forward'}
          size={18}
          color={ticketColors.ink3}
        />
      </TouchableOpacity>
    ) : null;

  return (
    <View style={isVariant ? { paddingLeft: 16 } : null}>
      <TicketListRow
        title={title}
        subtitle={subtitle}
        meta={metaParts.length > 0 ? metaParts.join(' · ') : null}
        leadingImage={item.imageUrl ?? null}
        leadingImageResizeMode="contain"
        value={qtyLabel}
        statusLabel={statusLabel}
        statusTone={statusTone}
        onPress={() => onDetails(item)}
        accessibilityLabel={`Detalle de stock de ${title}, ${qtyLabel}`}
        // Hit area táctil mínima 44px (WCAG): el pressable crece aunque la
        // fila tenga una sola línea de texto; el padding del row no cambia.
        pressableStyle={styles.rowPressable}
        footer={expandButton}
      />
    </View>
  );
}

export const InventoryStockCard = React.memo(InventoryStockCardInner);

const styles = StyleSheet.create({
  rowPressable: {
    minHeight: 44,
    justifyContent: 'center',
  },
});
